import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { TextView } from "@components";

import * as Styled from "./styles";
import { colors } from "../../utils";
import { searchItemVariants } from "./searchMotion";
import { resetFolders } from "../../redux/reducers/gallery";
import { COLLECTION_NOUN } from "../WheelGallery/WheelListing/FoldersV2/collectionsCopy";

// #920 §5 — one row in the global search's Collections tab.
//
// "A hit OPENS the collection": the row navigates to the collection's deep link
// (`/gallery?collection=<id>`), which FoldersV2/OwnerView consumes on arrival by
// opening that folder level. `resetFolders` mirrors UserItem's own navigate
// path — the gallery slice is per-gallery, and leaving the previous owner's tree
// in it would make the deep link resolve against stale folders.
//
// The row is deliberately text-only. A collection's cover is a thumbnail of a
// wheel INSIDE it, and the search endpoint returns folder rows without their
// wheels, so a cover here would either be a second request per row or a
// placeholder pretending to be a cover. The monogram tile carries the identity
// instead, using the same first-letter rule the gallery card falls back to.
const monogram = (title) => {
  const trimmed = String(title || "").trim();
  return trimmed ? trimmed[0].toUpperCase() : "#";
};

export default function CollectionItem({
  item,
  setSearchText = null,
  setShowResults = null,
  kbdNav = false,
  isHighlighted = false,
  optionId = undefined,
}) {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const reduceMotion = useReducedMotion();
  const itemVariants = searchItemVariants(reduceMotion);

  const open = () => {
    if (setSearchText) setSearchText("");
    if (setShowResults) setShowResults(false);
    dispatch(resetFolders());
    nav(item.deepLink);
  };

  const kbdProps = kbdNav
    ? {
        "data-search-result-item": "",
        role: "option",
        id: optionId,
        "aria-selected": isHighlighted,
        style: isHighlighted
          ? {
              backgroundColor: colors.Highlight,
              outline: `2px solid ${colors.PrimaryPurple500}`,
              outlineOffset: "-2px",
            }
          : undefined,
      }
    : {};

  return (
    <Styled.SearchItem
      data-testid="search-collection-row"
      onClick={open}
      variants={itemVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      {...kbdProps}
    >
      <div
        aria-hidden="true"
        style={{
          minWidth: 49,
          width: 49,
          height: 49,
          flexShrink: 0,
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 20,
          fontWeight: 700,
          color: colors.PrimaryPurple700,
          backgroundColor: colors.PrimaryPurple100,
        }}
      >
        {monogram(item.title)}
      </div>
      <Styled.SearchItemDetails>
        <TextView
          variant="subtitle"
          style={{ fontSize: 14, fontWeight: 600, WebkitLineClamp: 1 }}
        >
          {item.title}
        </TextView>
        <TextView
          color={colors.textGrey}
          style={{ fontSize: 13, WebkitLineClamp: 1 }}
        >
          {item.count == null
            ? COLLECTION_NOUN
            : `${COLLECTION_NOUN} · ${item.count} ${
                item.count === 1 ? "wheel" : "wheels"
              }`}
        </TextView>
      </Styled.SearchItemDetails>
    </Styled.SearchItem>
  );
}

CollectionItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    count: PropTypes.number,
    deepLink: PropTypes.string,
  }).isRequired,
  setSearchText: PropTypes.func,
  setShowResults: PropTypes.func,
  kbdNav: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  optionId: PropTypes.string,
};
